import * as React from 'react';
import cn from 'classnames';

import Typography from 'shared/newComponents/Typography';
import {
  TypographySize,
  TypographyType,
  TypographyWeight,
  TypographyColor
} from 'shared/newEntities/components/Typography';
import { SelectIcon } from 'shared/newComponents/icons';
import { IconSize } from 'shared/entities/components/Icon';
import Checkbox from 'shared/newComponents/form/Checkbox';
import CroppedContent from 'shared/newComponents/CroppedContent';
import MultiLineCroppedContent from 'shared/newComponents/MultiLineCroppedContent';
import Tag from 'shared/newComponents/Tag';
import { TagProps, TagSize } from 'shared/newEntities/components/Tag';
import Avatar from 'shared/newComponents/user/Avatar';
import {
  SelectorBaseItem,
  SelectorOptionSize,
  SelectorSize
} from 'shared/newEntities/components/Selector';
import { CommonDirection } from 'shared/entities/common/client';

import OptionRightComponent from './OptionRightComponent';

import './SelectorBaseOptionContent.modules.scss';

const fontSizeStepDown: Partial<Record<TypographySize, TypographySize>> = {
  [TypographySize.l]: TypographySize.m,
  [TypographySize.m]: TypographySize.s,
  [TypographySize.s]: TypographySize.xs,
  [TypographySize.xs]: TypographySize.xxs
};

type Props = SelectorBaseItem & {
  onClick: () => void;
  className?: string;
  selected?: boolean;
  selectView?: 'checkbox' | 'icon';
  largeOption?: boolean; // чтобы текст опции был максимально показан
  tag?: TagProps;
  selectorSize?: SelectorSize;
  fontSize?: TypographySize;
  fontWeight?: TypographyWeight;
  fontColor?: TypographyColor;
  smallDescription?: boolean;
  descriptionMaxLines?: 1 | 2 | 3;
};

const SelectorBaseOptionContent: React.FC<Props> = (props) => {
  const {
    title,
    onClick,
    selected,
    description,
    Icon,
    avatar,
    className,
    rightComponent,
    selectView = 'icon',
    largeOption = false,
    tagProps,
    disabled,
    selectorSize,
    extraComponent,
    fontSize,
    fontWeight,
    fontColor,
    smallDescription = false,
    descriptionMaxLines = 2
  }: Props = props;

  const handleCheckboxClick = React.useCallback(
    (_: boolean, originalEvent: React.MouseEvent<HTMLDivElement>) => {
      originalEvent.stopPropagation();
      onClick();
    },
    [onClick]
  );

  const withCheckbox = selectView === 'checkbox' && selected !== undefined;

  const showTitle = !(typeof title === 'string' && title.length === 0);

  const actualFontSize =
    fontSize ??
    (selectorSize === SelectorSize.small
      ? TypographySize.xs
      : TypographySize.m);

  const actualFontWeight =
    fontWeight ??
    (selectorSize === SelectorSize.small
      ? TypographyWeight.normal
      : TypographyWeight.heavy);

  const actualTitleColor =
    fontColor ??
    (disabled
      ? TypographyColor.tertiary
      : selectorSize === SelectorSize.small
        ? TypographyColor.secondary
        : TypographyColor.main);

  const actualOptionSize =
    selectorSize === SelectorSize.small
      ? SelectorOptionSize.m
      : SelectorOptionSize.l;

  const descriptionFontSize = smallDescription
    ? (fontSizeStepDown[actualFontSize] ?? actualFontSize)
    : actualFontSize;
  const isExtraLeft = extraComponent?.position === CommonDirection.left;
  const isExtraRight = extraComponent?.position === CommonDirection.right;
  const isExtraBottom = extraComponent?.position === CommonDirection.bottom;

  const extraComponentNode = extraComponent?.forOption ? (
    <div
      styleName={cn(
        'option__extra-component',
        isExtraRight && 'option__extra-component_align-right'
      )}
    >
      {extraComponent.forOption}
    </div>
  ) : null;

  const rightComponentNode = rightComponent && (
    <OptionRightComponent component={rightComponent} />
  );

  return (
    <div
      styleName={cn(
        'option',
        `option_${actualOptionSize}`,
        selected && 'option_selected',
        largeOption && 'option_large',
        disabled && 'option_disabled'
      )}
      onClick={onClick}
      className={className}
    >
      <div styleName="option__left">
        <div styleName="option__left-top">
          {withCheckbox && (
            <Checkbox
              styleName="option__checkbox"
              value={selected}
              onChange={handleCheckboxClick}
            />
          )}
          {isExtraLeft && extraComponentNode}
          {avatar ? (
            <Avatar {...avatar} styleName="option__icon" />
          ) : (
            Icon && <Icon styleName="option__icon" iconSize={IconSize.XM} />
          )}
          <div
            styleName={cn(
              'option__title-container',
              isExtraBottom && 'option__title-container_bottom'
            )}
          >
            {showTitle && (
              <CroppedContent
                popupContent={title}
                styleName={cn(
                  'option__title',
                  largeOption && 'option__title_large'
                )}
              >
                <Typography
                  styleName="option__title-text"
                  fontType={TypographyType.text}
                  fontSize={actualFontSize}
                  fontWeight={actualFontWeight}
                  fontColor={actualTitleColor}
                >
                  {title}
                </Typography>
              </CroppedContent>
            )}
            {tagProps && (
              <Tag styleName="option__tag" size={TagSize.xs} {...tagProps} />
            )}
            {!isExtraLeft && extraComponentNode}
          </div>
        </div>
        {description && (
          <MultiLineCroppedContent
            popupContent={description}
            maxLines={descriptionMaxLines}
            styleName={cn(
              'option__description',
              withCheckbox && 'option__description_with-select'
            )}
          >
            <Typography
              styleName="option__description-text"
              fontType={TypographyType.text}
              fontSize={descriptionFontSize}
              fontColor={TypographyColor.tertiary}
            >
              {description}
            </Typography>
          </MultiLineCroppedContent>
        )}
      </div>
      {selectView === 'icon' && selected && (
        <SelectIcon styleName="option__select-icon" iconSize={IconSize.XM} />
      )}
      {rightComponentNode}
    </div>
  );
};

export default React.memo(SelectorBaseOptionContent);
